import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import {
  getTemplate,
  getUiOptions,
  ArrayFieldTemplateProps,
  ArrayFieldTemplateItemType,
  FormContextType,
  RJSFSchema,
  StrictRJSFSchema
} from '@rjsf/utils';
import LabelValue from '../../LabelValue';
import { darken , Button } from '@mui/material';
import PolarisIcon from '../../PolarisIcon';
import SchemaTemplateSelect from '../SchemaTemplateSelect';

/** The `ArrayFieldTemplate` component is the template used to render all items in an array.
 *
 * @param props - The `ArrayFieldTemplateItemType` props for the component
 */

function singularize(word: string) {
  const endings: { [key: string]: string } = {
    ves: 'fe',
    ies: 'y',
    i: 'us',
    zes: 'ze',
    ses: 's',
    es: 'e',
    hes: 'h',
    s: ''
  };
  return word.replace(new RegExp(`(${Object.keys(endings).join('|')})$`), (r) => endings[r as keyof typeof endings]);
}

export default function ArrayFieldTemplate<T = any, S extends StrictRJSFSchema & { templates?: any } = RJSFSchema, F extends FormContextType = any>(
  props: ArrayFieldTemplateProps<T, S, F>
) {
  const { canAdd, disabled, idSchema, uiSchema, items, onAddClick, readonly, registry, required, schema, title, formContext } = props;
  const uiOptions = getUiOptions<T, S, F>(uiSchema);
  const ArrayFieldDescriptionTemplate = getTemplate<'ArrayFieldDescriptionTemplate', T, S, F>('ArrayFieldDescriptionTemplate', registry, uiOptions);
  const ArrayFieldItemTemplate = getTemplate<'ArrayFieldItemTemplate', T, S, F>('ArrayFieldItemTemplate', registry, uiOptions);
  const ArrayFieldTitleTemplate = getTemplate<'ArrayFieldTitleTemplate', T, S, F>('ArrayFieldTitleTemplate', registry, uiOptions);
  const hasTemplates = !!schema.templates;

  if (readonly && !items?.length) {
    return <LabelValue label={uiOptions.title || title}>-</LabelValue>;
  }

  if (readonly) {
    const areArrayValuesStrings = typeof schema.items === 'object' && !Array.isArray(schema.items) && schema.items.type === 'string';
    if (areArrayValuesStrings) {
      return (
        <LabelValue label={uiOptions.title || title}>
          {items?.map(({ children }: ArrayFieldTemplateItemType<T, S, F>) => children.props.formData).join(', ')}
        </LabelValue>
      );
    }
  }
  
  return (
    <Paper elevation={0} sx={{ backgroundColor: (theme) => darken(theme.palette.background.paper, 0.2) }}>
      <Box p={2}>
        {hasTemplates && (
          <SchemaTemplateSelect onChange={formContext?.onFormChange} templates={schema.templates} label={`${uiOptions.title || title} Templates`} />
        )}
        <ArrayFieldTitleTemplate
          idSchema={idSchema}
          title={uiOptions.title || title}
          schema={schema}
          uiSchema={uiSchema}
          required={required}
          registry={registry}
        />

        <ArrayFieldDescriptionTemplate
          idSchema={idSchema}
          description={uiOptions.description || schema.description}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />

        {items?.length
          ? items.map(({ key, ...itemProps }: ArrayFieldTemplateItemType<T, S, F>) => (
              <ArrayFieldItemTemplate
                key={key}
                {...{
                  ...itemProps,
                  children: {
                    ...itemProps.children,
                    props: {
                      ...itemProps.children.props,
                      name: singularize(title)
                    }
                  }
                }}
              />
            ))
          : '-'}
        {canAdd && !readonly && (
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Button variant="text" onClick={onAddClick} disabled={disabled} startIcon={<PolarisIcon icon="carbon:add" height={22} />}>
                {singularize(uiOptions.title || title)}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
}